import { supabase } from '../services/supabase';

/**
 * Generates upcoming event instances from active recurring schedules for a club.
 * Idempotent: uses (recurring_schedule_id, event_date) to prevent duplicates.
 * Creates events for the current week and next week.
 */
export async function generateRecurringEvents(clubId) {
  const { data: schedules, error: fetchErr } = await supabase
    .from('club_recurring_events')
    .select('*')
    .eq('club_id', clubId)
    .eq('is_active', true);

  if (fetchErr) {
    console.error('Error fetching recurring schedules:', fetchErr);
    return { created: 0, errors: [fetchErr.message] };
  }

  if (!schedules || schedules.length === 0) {
    return { created: 0, errors: [] };
  }

  let created = 0;
  const errors = [];

  for (const schedule of schedules) {
    const dates = getNextOccurrences(schedule.day_of_week, 2);

    for (const targetDate of dates) {
      const dateStr = formatDateISO(targetDate);

      const { data: existing } = await supabase
        .from('events')
        .select('id')
        .eq('recurring_schedule_id', schedule.id)
        .gte('event_date', dateStr + 'T00:00:00')
        .lte('event_date', dateStr + 'T23:59:59')
        .limit(1);

      if (existing && existing.length > 0) continue;

      const [startH, startM] = (schedule.start_time || '19:00').split(':').map(Number);
      const eventDateTime = new Date(targetDate);
      eventDateTime.setHours(startH, startM, 0, 0);

      let endDateTime = null;
      if (schedule.end_time) {
        const [endH, endM] = schedule.end_time.split(':').map(Number);
        endDateTime = new Date(targetDate);
        endDateTime.setHours(endH, endM, 0, 0);
      }

      const eventData = {
        club_id: schedule.club_id,
        title: schedule.title,
        description: schedule.description || null,
        event_type: schedule.event_type || 'CASUAL',
        event_date: eventDateTime.toISOString(),
        event_time: schedule.start_time,
        end_time: endDateTime ? endDateTime.toISOString() : null,
        location: schedule.location_address || schedule.location_name || null,
        location_address: schedule.location_address || null,
        location_latitude: schedule.location_latitude || null,
        location_longitude: schedule.location_longitude || null,
        capacity: schedule.capacity || null,
        is_paid: schedule.is_paid || false,
        price: schedule.price || null,
        recurring_schedule_id: schedule.id,
        is_generated: true,
      };

      const { error: insertErr } = await supabase
        .from('events')
        .insert([eventData]);

      if (insertErr) {
        if (insertErr.code === '23505') continue; // unique constraint - already exists
        errors.push(`Schedule "${schedule.title}" for ${dateStr}: ${insertErr.message}`);
      } else {
        created++;
      }
    }

    await supabase
      .from('club_recurring_events')
      .update({ last_generated_for_date: formatDateISO(new Date()) })
      .eq('id', schedule.id);
  }

  return { created, errors };
}

function getNextOccurrences(dayOfWeek, count) {
  const dates = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let current = new Date(today);
  while (dates.length < count) {
    if (current.getDay() === dayOfWeek) {
      if (current >= today) {
        dates.push(new Date(current));
      }
    }
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function formatDateISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
